class DLBFFeaturedDealsNavigationM extends HTMLElement {
    // 常量定义
    static CONSTANTS = {
        SCROLL_OFFSET: 80,
        NAVIGATION_PADDING: 20,
        THROTTLE_DELAY: 16, // ~60fps
        RESIZE_DEBOUNCE_DELAY: 150
    };

    constructor() {
        super();
        this.navigationList = null;
        this.navigationItems = null;
        this.navigationTitles = null;
        this.slider = null;
        this.currentIndex = 0;
        this.initialTop = 0;
        
        // 绑定方法上下文
        this.handleScroll = this.throttle(this.handleScroll.bind(this), DLBFFeaturedDealsNavigationM.CONSTANTS.THROTTLE_DELAY);
        this.handleResize = this.debounce(this.handleResize.bind(this), DLBFFeaturedDealsNavigationM.CONSTANTS.RESIZE_DEBOUNCE_DELAY);
        
        // 缓存section引用，避免重复查询
        this.section = null;
        
        // 存储事件处理器，用于后续移除
        this.eventHandlers = new Map();

        // 粘性Tab
        this.stickyTab = this.getAttribute('data-sticky-tab') === 'true';
    }

    connectedCallback() {
        try {
            // 初始化元素引用
            if (!this.initializeElements()) {
                console.warn('DLBFFeaturedDealsNavigationM: Failed to initialize elements');
                return;
            }

            // 缓存section引用
            this.section = this.closest('.dl-bf-featured-deals-m');

            // 创建滑动条
            this.createSlider();

            // 找到初始active项的索引
            const activeIndex = Array.from(this.navigationTitles).findIndex(title => 
                title.classList.contains('active')
            );
            this.currentIndex = activeIndex >= 0 ? activeIndex : 0;

            // 初始化滑动条位置（使用requestAnimationFrame确保DOM已渲染）
            requestAnimationFrame(() => {
                this.updateSliderPosition(this.currentIndex);
            });

            // 绑定点击事件
            this.bindEvents();

            // 监听窗口大小变化，重新计算位置
            window.addEventListener('resize', this.handleResize, { passive: true });

            if (this.stickyTab) {
                // 初始化吸顶功能
                this.initStickyBehavior();
            }
        } catch (error) {
            console.error('DLBFFeaturedDealsNavigationM initialization error:', error);
        }
    }

    /**
     * 初始化所有必需的DOM元素
     * @returns {boolean} 是否成功初始化
     */
    initializeElements() {
        this.navigationList = this.querySelector('.dl-bf-featured-deals-navigation-m__list');
        this.navigationItems = this.querySelectorAll('.dl-bf-featured-deals-navigation-m__item');
        this.navigationTitles = this.querySelectorAll('.dl-bf-featured-deals-navigation-m__item-title');

        return this.navigationList && 
               this.navigationItems?.length > 0 && 
               this.navigationTitles?.length > 0;
    }

    createSlider() {
        // 创建滑动条元素
        this.slider = document.createElement('div');
        this.slider.className = 'dl-bf-featured-deals-navigation-m__slider';
        this.navigationList.appendChild(this.slider);
    }

    bindEvents() {
        if (!this.navigationItems || this.navigationItems.length === 0) {
            return;
        }

        this.navigationItems.forEach((item, index) => {
            // 创建并存储点击事件处理器
            const clickHandler = () => this.handleItemClick(index);
            this.eventHandlers.set(`click-${index}`, clickHandler);
            item.addEventListener('click', clickHandler);

            // 创建并存储键盘事件处理器（可访问性）
            const keydownHandler = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleItemClick(index);
                }
            };
            this.eventHandlers.set(`keydown-${index}`, keydownHandler);
            item.addEventListener('keydown', keydownHandler);

            // 设置可访问性属性
            item.setAttribute('tabindex', index.toString());
            item.setAttribute('role', 'tab');
            item.setAttribute('aria-selected', index === this.currentIndex ? 'true' : 'false');
        });
    }

    handleItemClick(index) {
        // 避免重复点击同一项
        if (index === this.currentIndex || !this.isValidIndex(index)) return;

        // 更新active状态
        this.updateActiveStates(index);

        // 更新滑动条位置
        this.updateSliderPosition(index);
        this.currentIndex = index;

        // 滚动导航栏，使当前 tab 可见
        this.scrollNavigationIntoView(index);

        // 切换 tab content
        this.switchTabContent(index);

        // 等待 DOM 更新后再滚动，避免内容高度变化导致的跳动
        requestAnimationFrame(() => {
            this.scrollToSectionTop();
        });

        // 触发自定义事件，以便其他组件可以监听
        this.dispatchTabChangeEvent(index);
    }

    /**
     * 验证索引是否有效
     * @param {number} index 
     * @returns {boolean}
     */
    isValidIndex(index) {
        return index >= 0 && index < this.navigationTitles.length;
    }

    /**
     * 更新active状态
     * @param {number} index 
     */
    updateActiveStates(index) {
        // 移除所有 active 类并更新 aria-selected
        this.navigationTitles.forEach(title => {
            title.classList.remove('active');
        });
        
        this.navigationItems.forEach(item => {
            item.setAttribute('aria-selected', 'false');
        });

        // 添加 active 类到当前点击的项
        if (this.navigationTitles[index]) {
            this.navigationTitles[index].classList.add('active');
        }
        
        if (this.navigationItems[index]) {
            this.navigationItems[index].setAttribute('aria-selected', 'true');
        }
    }

    /**
     * 切换tab内容显示
     * @param {number} index 
     */
    switchTabContent(index) {
        // 使用缓存的section引用
        if (!this.section) return;

        const tabContents = this.section.querySelectorAll('.dl-bf-featured-deals-m__tab-content');
        if (!tabContents.length) return;
        
        // 移除所有 active 类
        tabContents.forEach(content => {
            content.classList.remove('active');
        });

        // 添加 active 类到对应的 tab content
        const targetContent = Array.from(tabContents).find(content => {
            const contentIndex = parseInt(content.getAttribute('data-dl-tab-index'), 10);
            return contentIndex === index;
        });

        if (targetContent) {
            targetContent.classList.add('active');
        }
    }

    /**
     * 触发tab切换事件
     * @param {number} index 
     */
    dispatchTabChangeEvent(index) {
        this.dispatchEvent(new CustomEvent('tab-change', {
            detail: { 
                index, 
                title: this.navigationTitles[index]?.textContent?.trim() 
            },
            bubbles: true
        }));
    }

    updateSliderPosition(index) {
        if (!this.slider || !this.navigationItems[index]) return;

        const targetItem = this.navigationItems[index];
        const listRect = this.navigationList.getBoundingClientRect();
        const itemRect = targetItem.getBoundingClientRect();

        // 计算滑动条的位置和宽度
        const left = itemRect.left - listRect.left;
        const width = itemRect.width;

        // 使用 transform 移动滑动条，性能更好，动画更流畅
        this.slider.style.width = `${width}px`;
        this.slider.style.transform = `translateX(${left}px)`;
    }

    initStickyBehavior() {
        // 记录导航栏的初始位置
        requestAnimationFrame(() => {
            const rect = this.getBoundingClientRect();
            this.initialTop = rect.top + window.pageYOffset;
            
            // 添加滚动监听
            window.addEventListener('scroll', this.handleScroll, { passive: true });
            
            // 初始检查一次（防止页面加载时已经滚动了）
            this.handleScroll();
        });
    }

    handleScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // 当滚动位置超过导航栏初始位置时，添加sticky class
        if (scrollTop >= this.initialTop) {
            this.classList.add('dl-bf-featured-deals-navigation-m__sticky');
        } else {
            this.classList.remove('dl-bf-featured-deals-navigation-m__sticky');
        }
    }

    /**
     * 使导航项在容器中可见
     * @param {number} index 
     */
    scrollNavigationIntoView(index) {
        const targetItem = this.navigationItems[index];
        if (!targetItem) return;

        // 获取容器和目标项的位置
        const containerRect = this.getBoundingClientRect();
        const itemRect = targetItem.getBoundingClientRect();

        // 计算 item 相对于容器的位置
        const itemLeft = itemRect.left - containerRect.left;
        const itemRight = itemRect.right - containerRect.left;
        
        const { NAVIGATION_PADDING } = DLBFFeaturedDealsNavigationM.CONSTANTS;
        
        // 如果 item 右侧超出可视区域，向左滚动
        if (itemRight > containerRect.width) {
            const scrollAmount = itemRight - containerRect.width + NAVIGATION_PADDING;
            this.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
        // 如果 item 左侧超出可视区域，向右滚动
        else if (itemLeft < 0) {
            this.scrollBy({
                left: itemLeft - NAVIGATION_PADDING,
                behavior: 'smooth'
            });
        }
    }

    /**
     * 滚动到section顶部
     */
    scrollToSectionTop() {
        // 使用初始化时记录的导航栏原始位置
        // 因为导航栏是 sticky 的，getBoundingClientRect() 在吸顶时不准确
        if (!this.initialTop) return;

        const { SCROLL_OFFSET } = DLBFFeaturedDealsNavigationM.CONSTANTS;
        
        // 平滑滚动到导航栏的原始位置
        window.scrollTo({
            top: this.initialTop - SCROLL_OFFSET,
            behavior: 'smooth'
        });
    }

    /**
     * 处理窗口大小变化
     */
    handleResize() {
        this.updateSliderPosition(this.currentIndex);
        const rect = this.getBoundingClientRect();
        this.initialTop = rect.top + window.pageYOffset;
    }

    /**
     * 节流函数 - 限制函数执行频率
     * @param {Function} func 
     * @param {number} delay 
     * @returns {Function}
     */
    throttle(func, delay) {
        let lastCall = 0;
        return function(...args) {
            const now = Date.now();
            if (now - lastCall >= delay) {
                lastCall = now;
                return func.apply(this, args);
            }
        };
    }

    /**
     * 防抖函数 - 延迟执行，多次调用只执行最后一次
     * @param {Function} func 
     * @param {number} delay 
     * @returns {Function}
     */
    debounce(func, delay) {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    }

    /**
     * 组件销毁时清理资源
     */
    disconnectedCallback() {
        // 移除全局事件监听，避免内存泄漏
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        
        // 清理导航项事件监听器
        if (this.navigationItems) {
            this.navigationItems.forEach((item, index) => {
                const clickHandler = this.eventHandlers.get(`click-${index}`);
                const keydownHandler = this.eventHandlers.get(`keydown-${index}`);
                
                if (clickHandler) {
                    item.removeEventListener('click', clickHandler);
                }
                if (keydownHandler) {
                    item.removeEventListener('keydown', keydownHandler);
                }
            });
        }
        
        // 清空事件处理器映射
        this.eventHandlers.clear();
    }
}

customElements.define('dl-bf-featured-deals-navigation-m', DLBFFeaturedDealsNavigationM);